import React, { memo } from 'react'
import { IconButton, styled, Tooltip, tooltipClasses, TooltipProps, useTheme } from '@mui/material'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import { ShortcutKeys, ShortcutTooltipTitle } from '../../KeyboardShortcuts/ShortcutTooltipTitle'
import { isMacOS } from '../../../util/general'

const ScrollToRecentTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} disableInteractive />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 'none',
    },
})

type ScrollToRecentBarButtonProps = {
    visible: boolean
    onClick: () => void
    priceScaleWidth: number
    timeScaleHeight: number
}

const SCROLL_TO_RECENT_SHORTCUT_KEYS = isMacOS() ? (['⌥', '⇧', '→'] as const) : (['Alt', '⇧', '→'] as const)

const ScrollToRecentBarButton = ({ visible, onClick, priceScaleWidth, timeScaleHeight }: ScrollToRecentBarButtonProps) => {
    const theme = useTheme()
    const isDark = theme.palette.mode === 'dark'

    return (
        <ScrollToRecentTooltip
            arrow
            placement="top"
            title={
                <ShortcutTooltipTitle label="Scroll to the most recent bar">
                    <ShortcutKeys keys={[...SCROLL_TO_RECENT_SHORTCUT_KEYS]} />
                </ShortcutTooltipTitle>
            }
        >
            <IconButton
                aria-label="Scroll to the most recent bar"
                size="small"
                onClick={onClick}
                sx={{
                    position: 'absolute',
                    zIndex: 2,
                    bottom: timeScaleHeight + 12,
                    right: priceScaleWidth + 12,
                    width: 28,
                    height: 28,
                    minWidth: 28,
                    p: 0,
                    borderRadius: '4px',
                    backgroundColor: isDark ? '#2a2e39' : '#ffffff',
                    color: isDark ? '#d1d4dc' : '#131722',
                    border: isDark ? '1px solid #434651' : '1px solid #e0e3eb',
                    boxShadow: isDark ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.08)',
                    transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out, background-color 0.15s ease',
                    transform: visible ? 'scale(1)' : 'scale(0)',
                    opacity: visible ? 1 : 0,
                    pointerEvents: visible ? 'auto' : 'none',
                    '&:hover': {
                        backgroundColor: isDark ? '#363a45' : '#f0f3fa',
                    },
                }}
            >
                <KeyboardDoubleArrowRightIcon sx={{ fontSize: 18 }} />
            </IconButton>
        </ScrollToRecentTooltip>
    )
}

export default memo(ScrollToRecentBarButton)
